import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { User, ShoppingBag, CreditCard, ArrowLeft, Edit2, Save, X, Package, MessageCircle, Mail } from "lucide-react";
import { ShopMessageThread } from "@/components/ShopMessageThread";
import { Switch } from "@/components/ui/switch";

export default function ParentProfile() {
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ptaId = searchParams.get('pta');
  const defaultTab = searchParams.get('tab') || 'listings';
  
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<any>(null);
  const [parentData, setParentData] = useState<any>(null);
  const [ptaData, setPtaData] = useState<any>(null);
  const [listings, setListings] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [editing, setEditing] = useState(false);
  const [fullName, setFullName] = useState("");
  const [selectedMessageItem, setSelectedMessageItem] = useState<any>(null);
  const [newsletterSubscribed, setNewsletterSubscribed] = useState<boolean | null>(null);
  const [newsletterSubscriberId, setNewsletterSubscriberId] = useState<string | null>(null);
  const [newsletterLoading, setNewsletterLoading] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (user) {
      fetchParentData();
    }
  }, [user]);

  useEffect(() => {
    if (parentData?.pta_id) {
      fetchPtaData();
      fetchListings();
      fetchMessages();
      fetchNewsletterStatus();
    }
  }, [parentData?.pta_id]);

  const checkAuth = async () => {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      navigate('/portal');
      return;
    }
    setUser(user);
  };

  const fetchParentData = async () => {
    if (!user) return;
    
    // Build query - filter by pta_id if provided in URL
    let query = supabase
      .from('parents')
      .select('*')
      .eq('user_id', user.id);
    
    if (ptaId) {
      query = query.eq('pta_id', ptaId);
    }
    
    const { data, error } = await query.maybeSingle();
    
    if (error) {
      console.error('Error fetching parent data:', error);
      toast({
        title: "Error",
        description: "Could not load your profile",
        variant: "destructive"
      });
    }
    
    if (data) {
      setParentData(data);
      setFullName(data.full_name || '');
    }
    setLoading(false);
  };

  const fetchPtaData = async () => {
    if (!parentData?.pta_id) return;
    
    // Use RPC function to get header_color and other public info
    const { data } = await supabase
      .rpc('get_public_pta_info', { p_pta_id: parentData.pta_id })
      .single();
    
    if (data) {
      setPtaData(data);
    }
  };

  const fetchListings = async () => {
    if (!parentData?.id) return;
    
    const { data } = await supabase
      .from('shop_items')
      .select('*')
      .eq('seller_parent_id', parentData.id)
      .order('created_at', { ascending: false });
    
    if (data) {
      setListings(data);
    }
  };

  const fetchNewsletterStatus = async () => {
    if (!parentData?.id || !parentData?.pta_id) return;
    const { data } = await supabase
      .from('newsletter_subscribers')
      .select('id, is_subscribed')
      .eq('parent_id', parentData.id)
      .eq('pta_id', parentData.pta_id)
      .maybeSingle();
    if (data) {
      setNewsletterSubscribed(data.is_subscribed);
      setNewsletterSubscriberId(data.id);
    } else {
      setNewsletterSubscribed(false);
      setNewsletterSubscriberId(null);
    }
  };

  const handleToggleNewsletter = async (subscribe: boolean) => {
    if (!parentData?.id || !parentData?.pta_id || !user) return;
    setNewsletterLoading(true);
    try {
      if (subscribe && !newsletterSubscriberId) {
        await supabase.from('newsletter_subscribers').insert({
          pta_id: parentData.pta_id,
          parent_id: parentData.id,
          email: user.email,
        });
      } else if (newsletterSubscriberId) {
        await supabase.from('newsletter_subscribers')
          .update({ is_subscribed: subscribe, unsubscribed_at: subscribe ? null : new Date().toISOString() })
          .eq('id', newsletterSubscriberId);
      }
      toast({ title: subscribe ? "Subscribed to newsletter" : "Unsubscribed from newsletter" });
      fetchNewsletterStatus();
    } catch (err: any) {
      toast({ title: "Error", description: err.message, variant: "destructive" });
    } finally {
      setNewsletterLoading(false);
    }
  };

  const fetchMessages = async () => {
    if (!parentData?.id || !user?.id) return;
    
    // Get all shop items where this parent is involved in messages (either as seller or buyer)
    const { data: itemsWithMessages } = await supabase
      .from('shop_messages')
      .select(`
        shop_item_id,
        is_read,
        created_at,
        sender_user_id,
        shop_items!inner(id, name, seller_parent_id, seller_user_id)
      `)
      .or(`sender_user_id.eq.${user.id},shop_items.seller_user_id.eq.${user.id}`)
      .order('created_at', { ascending: false });

    if (itemsWithMessages) {
      // Group by shop_item_id and get unique items with unread counts
      const itemMap = new Map();
      itemsWithMessages.forEach((msg: any) => {
        const itemId = msg.shop_item_id;
        if (!itemMap.has(itemId)) {
          itemMap.set(itemId, {
            id: msg.shop_items.id,
            name: msg.shop_items.name,
            lastMessageAt: msg.created_at,
            unreadCount: 0
          });
        }
        // Count unread messages not sent by current user
        if (!msg.is_read && msg.sender_user_id !== user.id) {
          const item = itemMap.get(itemId);
          item.unreadCount++;
        }
      });
      setMessages(Array.from(itemMap.values()));
    }
  };

  const handleSaveProfile = async () => {
    if (!parentData?.id) return;
    
    const { error } = await supabase
      .from('parents')
      .update({ full_name: fullName })
      .eq('id', parentData.id);
    
    if (error) {
      toast({
        title: "Error",
        description: "Could not update your profile",
        variant: "destructive"
      });
    } else {
      toast({
        title: "Profile updated",
        description: "Your changes have been saved"
      });
      setEditing(false);
      fetchParentData();
    }
  };

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/portal');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  if (!parentData) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Card className="max-w-md">
          <CardHeader>
            <CardTitle>Profile Not Found</CardTitle>
            <CardDescription>You haven't registered as a parent yet.</CardDescription>
          </CardHeader>
          <CardContent>
            <Button onClick={() => navigate('/portal')}>
              Register as Parent
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex flex-col">
      {/* Header */}
      <header 
        className="border-b border-border backdrop-blur sticky top-0 z-50"
        style={{ backgroundColor: ptaData?.header_color ? `${ptaData.header_color}` : undefined }}
      >
        <div className="container flex h-20 items-center justify-between">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              onClick={() => navigate(parentData?.pta_id ? `/fundraising/${parentData.pta_id}` : '/')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex items-center gap-2">
              {ptaData?.logo_url ? (
                <img src={ptaData.logo_url} alt={ptaData?.name || 'PTA Logo'} className="h-12 w-auto rounded object-cover" />
              ) : (
                <span className="font-semibold">{ptaData?.name || 'Parent Portal'}</span>
              )}
            </div>
          </div>
          <Button variant="outline" onClick={handleSignOut}>
            Sign Out
          </Button>
        </div>
      </header>

      <main className="container py-8 max-w-4xl">
        <h1 className="text-3xl font-bold mb-8">My Profile</h1>

        {/* Profile Card */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-4">
                <Avatar className="h-16 w-16">
                  <AvatarImage src={user?.user_metadata?.avatar_url} />
                  <AvatarFallback className="bg-primary text-primary-foreground text-xl">
                    {(parentData.full_name || user?.email)?.charAt(0).toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <div>
                  {editing ? (
                    <div className="flex items-center gap-2">
                      <Input 
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        className="w-48"
                      />
                      <Button size="icon" variant="ghost" onClick={handleSaveProfile}>
                        <Save className="h-4 w-4" />
                      </Button>
                      <Button size="icon" variant="ghost" onClick={() => {
                        setEditing(false);
                        setFullName(parentData.full_name || '');
                      }}>
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ) : (
                    <div className="flex items-center gap-2">
                      <CardTitle>{parentData.full_name || 'Parent'}</CardTitle>
                      <Button size="icon" variant="ghost" onClick={() => setEditing(true)}>
                        <Edit2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                  <CardDescription>{user?.email}</CardDescription>
                </div>
              </div>
              {ptaData?.show_marketplace_section && (
                <div className="text-right">
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <CreditCard className="h-4 w-4" />
                    <span>Credits: <span className="font-semibold text-foreground">{parentData.credit_balance || 0}</span></span>
                  </div>
                  <Button 
                    variant="link" 
                    size="sm" 
                    className="p-0 h-auto"
                    onClick={() => navigate(`/marketplace/buy-credits?pta=${parentData.pta_id}`)}
                  >
                    Buy More Credits
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
        </Card>

        {/* Tabs - only show marketplace tabs if enabled */}
        <Tabs defaultValue={ptaData?.show_marketplace_section ? defaultTab : 'profile'} className="w-full">
          <TabsList className={`grid w-full ${ptaData?.show_marketplace_section ? 'grid-cols-4' : 'grid-cols-1'}`}>
            <TabsTrigger value="profile" className="gap-2">
              <User className="h-4 w-4" />
              Profile
            </TabsTrigger>
            {ptaData?.show_marketplace_section && (
              <>
                <TabsTrigger value="listings" className="gap-2">
                  <ShoppingBag className="h-4 w-4" />
                  My Listings
                </TabsTrigger>
                <TabsTrigger value="messages" className="gap-2 relative">
                  <MessageCircle className="h-4 w-4" />
                  Messages
                  {messages.filter(m => m.unreadCount > 0).length > 0 && (
                    <span className="absolute -top-1 -right-1 h-4 w-4 rounded-full bg-destructive text-destructive-foreground text-xs flex items-center justify-center">
                      {messages.filter(m => m.unreadCount > 0).length}
                    </span>
                  )}
                </TabsTrigger>
                <TabsTrigger value="purchases" className="gap-2">
                  <Package className="h-4 w-4" />
                  Purchases
                </TabsTrigger>
              </>
            )}
          </TabsList>

          <TabsContent value="profile" className="mt-6">
            <h2 className="text-xl font-semibold mb-4">Your Information</h2>
            <Card>
              <CardContent className="py-6">
                <div className="space-y-4">
                  <div>
                    <Label className="text-muted-foreground">Name</Label>
                    <p className="font-medium">{parentData.full_name || 'Not set'}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Email</Label>
                    <p className="font-medium">{user?.email}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">PTA</Label>
                    <p className="font-medium">{ptaData?.name || 'N/A'}</p>
                  </div>
                </div>

                {/* Newsletter Section */}
                {newsletterSubscribed !== null && (
                  <div className="mt-6 pt-6 border-t border-border">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Mail className="h-5 w-5 text-primary" />
                        <div>
                          <p className="font-medium">Newsletter</p>
                          <p className="text-sm text-muted-foreground">
                            {newsletterSubscribed ? `You're subscribed to the ${ptaData?.name || 'PTA'} newsletter` : 'Subscribe to receive news and updates'}
                          </p>
                        </div>
                      </div>
                      <Switch
                        checked={newsletterSubscribed}
                        onCheckedChange={handleToggleNewsletter}
                        disabled={newsletterLoading}
                      />
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {ptaData?.show_marketplace_section && (
            <TabsContent value="listings" className="mt-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-xl font-semibold">Your Marketplace Listings</h2>
                <Button onClick={() => navigate(`/marketplace/create?pta=${parentData.pta_id}`)}>
                  Create Listing
                </Button>
              </div>

              {listings.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <ShoppingBag className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground mb-4">You haven't created any listings yet</p>
                    <Button onClick={() => navigate(`/marketplace/create?pta=${parentData.pta_id}`)}>
                      Create Your First Listing
                    </Button>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid gap-4 md:grid-cols-2">
                  {listings.map((item) => (
                    <Card key={item.id}>
                      <CardHeader className="pb-2">
                        <div className="flex items-start justify-between">
                          <div>
                            <CardTitle className="text-lg">{item.name}</CardTitle>
                            <CardDescription>£{item.price?.toFixed(2)}</CardDescription>
                          </div>
                          <Badge variant={item.status === 'active' ? 'default' : 'secondary'}>
                            {item.status}
                          </Badge>
                        </div>
                      </CardHeader>
                      <CardContent>
                        <p className="text-sm text-muted-foreground line-clamp-2">{item.description}</p>
                        <div className="flex gap-2 mt-4">
                          <Button 
                            variant="outline" 
                            size="sm"
                            onClick={() => navigate(`/marketplace?pta=${parentData.pta_id}&item=${item.id}`)}
                          >
                            View
                          </Button>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          )}

          {ptaData?.show_marketplace_section && (
            <TabsContent value="messages" className="mt-6">
              <h2 className="text-xl font-semibold mb-4">Your Messages</h2>
              {messages.length === 0 ? (
                <Card>
                  <CardContent className="py-12 text-center">
                    <MessageCircle className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                    <p className="text-muted-foreground">No messages yet</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="space-y-3">
                  {messages.map((item) => (
                    <Card 
                      key={item.id} 
                      className="cursor-pointer hover:bg-accent/50 transition-colors"
                      onClick={() => setSelectedMessageItem(item)}
                    >
                      <CardContent className="py-4 flex items-center justify-between">
                        <div>
                          <p className="font-medium">{item.name}</p>
                          <p className="text-sm text-muted-foreground">
                            Last message: {new Date(item.lastMessageAt).toLocaleDateString()}
                          </p>
                        </div>
                        {item.unreadCount > 0 && (
                          <Badge variant="destructive">{item.unreadCount} unread</Badge>
                        )}
                      </CardContent>
                    </Card>
                  ))}
                </div>
              )}
            </TabsContent>
          )}

          {ptaData?.show_marketplace_section && (
            <TabsContent value="purchases" className="mt-6">
              <h2 className="text-xl font-semibold mb-4">Your Purchases</h2>
              <Card>
                <CardContent className="py-12 text-center">
                  <Package className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                  <p className="text-muted-foreground">No purchases yet</p>
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>

        {/* Message Thread Dialog */}
        {selectedMessageItem && (
          <ShopMessageThread
            itemId={selectedMessageItem.id}
            itemName={selectedMessageItem.name}
            open={!!selectedMessageItem}
            onOpenChange={(open) => !open && setSelectedMessageItem(null)}
          />
        )}
      </main>

      {/* Footer */}
      <footer className="bg-card border-t border-border mt-auto">
        <div className="container py-8">
          <div className="border-t border-border pt-8 text-center text-sm text-foreground">
            <p>© {new Date().getFullYear()} The PTA Hub. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
